import React from 'react';

import withPageComponents from '../../page/components/with-page-components';
import JsonLd from '../../shared/components/json-ld';
import {
    getProductJsonLdImageArrayData,
    getProductJsonLdData,
    getProductJsonLdOffers,
} from '../../product/components/product-json-ld';

const nomralizelLabel = label => {
    if (!label) return '';
    return label.toLowerCase().replace(/\s/g, '-');
};

const mapProperties = (data, exclusionMap) => {
    return data.reduce((acc, item) => {
        if (!item || !item.label || !item.value || (exclusionMap && exclusionMap[item.label] === item.value)) {
            return acc;
        }
        acc.push({
            '@type': 'PropertyValue',
            name: item.label,
            value: item.value,
        });
        return acc;
    }, []);
};

function convertToSchemaProductGroup(data, seo = {}, prices = {}) {
    try {
        const { shadowCTOFilter, shadowCTOSkus, shadowCTOConfig, linkUrl, image } = data || {};
        if (!shadowCTOSkus) {
            return null;
        }
        const { label: filterLabel } = shadowCTOFilter || {};
        const skuObj = shadowCTOFilter
            ? shadowCTOFilter?.value?.reduce((acc, item) => {
                  const group = shadowCTOSkus[item];
                  return { ...acc, ...group };
              }, {})
            : shadowCTOSkus;

        const variants = Object.keys(skuObj)
            .map(key => {
                return { sku: key, ...skuObj[key] };
            })
            .filter(prd => prd.sku !== data.sku); //remove master sku from variants

        if (!variants || variants.length === 0) {
            return null;
        }

        const baseSku = skuObj[data.sku] || {};
        //create label to value map for exclusion
        const exclusionMap = baseSku?.shadowCTOSpecs?.reduce((acc, item) => {
            if (item && item.label && item.value) {
                acc[item.label] = item.value;
            }
            return acc;
        }, {});

        const variesBy = shadowCTOConfig?.reduce(
            (acc, config) => {
                if (!config?.label) {
                    return acc;
                }
                acc.push(`schema.org/${nomralizelLabel(config.label)}`);
                return acc;
            },
            filterLabel ? [`schema.org/${nomralizelLabel(filterLabel)}`] : [],
        );

        const productGroup = {
            '@context': 'https://schema.org',
            '@type': 'ProductGroup',
            name: data.name,
            image,
            productGroupID: data.sku,
            description: seo.description || data.shortDescription || data.longDescription || '',
            ...(baseSku?.shadowCTOSpecs?.length > 0
                ? { additionalProperty: mapProperties(baseSku.shadowCTOSpecs) }
                : {}),
            variesBy,
            url: linkUrl,
            mpn: data.sku,
            sku: data.sku,
            brand: {
                '@type': 'Brand',
                name: 'HP',
            },
            manufacturer: {
                '@type': 'Organization',
                name: 'HP',
            },
            hasVariant: variants.map(prd => {
                const price = prices[prd.catentryId];
                const { name, images } = price || {};
                return {
                    '@type': 'Product',
                    description: prd.description,
                    brand: {
                        '@type': 'Brand',
                        name: 'HP',
                    },
                    url: prd.url,
                    sku: prd.sku,
                    ...(name && { name }),
                    ...(getProductJsonLdImageArrayData(images, { ...(prd || {}), ...(price || {}) }) || {}),
                    ...(getProductJsonLdOffers({
                        sku: prd.sku,
                        productUrl: prd.url,
                        price,
                    }) || {}),
                };
            }),
        };

        return productGroup;
    } catch (error) {
        return null;
    }
}

export default withPageComponents(
    ({
        data,
        renderInHead,
        productInitial: product,
        pdpImages,
        productInitialPrice: price,
        seo,
        pdpReviewsStructuredData,
    }) => {
        const schemaData = [...(data || [])];
        let parsedImages = getProductJsonLdImageArrayData(
            pdpImages?.mediumImages || pdpImages?.fullImages || pdpImages?.zoomImages || pdpImages?.thumbnailImages,
            product,
        );
        let { image } = parsedImages || {};
        const productGroup = convertToSchemaProductGroup({ ...product, image }, seo, price?.variantPrices);
        if (product && product.name) {
            schemaData.push({
                type: 'product',
                data: getProductJsonLdData({ ...product, image, price }, seo, pdpReviewsStructuredData),
            });
        }

        if (productGroup) {
            schemaData.push({ type: 'product-group', data: productGroup });
        }

        return schemaData.length > 0 ? (
            <JsonLd data={schemaData} shouldNotUseDefaults={true} renderInHead={renderInHead} />
        ) : null;
    },
    { components: ['productInitial', 'pdpImages', 'productInitialPrice', 'pdpReviewsStructuredData'], seo: true },
);
